import {
    Dispatch,
    ReactNode,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { ArrayItemError } from "../utils/formValidators";
import { serializeToJsonIncludingUndefined, toTitleCase } from "../utils";
import { InputLabel, OptionType, PropsInput, DropdownProps } from "../components";
import { FormErrorDisplay } from "../components/FormError/FormError";

export type PartialRecord<K extends keyof any, T> = {
    [P in K]?: T;
};
export type FormError = string | ArrayItemError;
export type FormValidators<T> = PartialRecord<
    keyof T,
    Array<(property: keyof T, formData: T) => Promise<FormError | FormError[] | undefined>>
>;
export type FormErrors<T> = PartialRecord<keyof T, FormError | FormError[]>;

interface FormClasses {
    label?: string;
    input?: string;
    inputContainer?: string;
    dropdown?: string;
    dropdownContainer?: string;
}
interface UseFormOptions<T> {
    initialValue: T;
    valueToEdit?: T;
    validators?: FormValidators<T>;
    simpleDirtyCheck?: boolean;
    classes?: FormClasses;
    setCleanup?: Dispatch<SetStateAction<() => void>>;
}

type HandledInputProps = Pick<
    PropsInput,
    "id" | "name" | "isValid" | "errorMessage" | "onChange" | "size" | "className" | "type" | "value"
>;
type HandledDropdownProps<TOption> = Pick<
    DropdownProps<TOption>,
    | "id"
    | "name"
    | "isValid"
    | "errorMessage"
    | "onChange"
    | "size"
    | "className"
    | "options"
    | "value"
    | "placeholder"
>;

export interface UseFormReturn<T> {
    formData: T;
    setFormData: Dispatch<SetStateAction<T>>;
    isFormDirty: boolean;
    errors: FormErrors<T>;
    hasErrors: boolean;
    setErrors: Dispatch<SetStateAction<FormErrors<T>>>;
    onFormChange: (diff: Partial<T>, affectsDirty?: boolean) => void;
    changeFormData: (change: (previousData: T) => T, affectsDirty?: boolean) => void;
    validateForm: (propertiesToValidate?: (keyof T)[]) => Promise<boolean>;
    validateData: (
        data: T,
        propertiesToValidate?: (keyof T)[],
        changeState?: boolean
    ) => Promise<boolean>;
    cleanUpForm: () => void;
    resetFormDirty: () => void;
    resetDataToInitialState: () => void;
    labelFor: (what: keyof T, label?: string) => ReactNode;
    inputPropsFor: (what: keyof T) => HandledInputProps;
    dropdownPropsFor: <TOption>(
        what: keyof T,
        options: OptionType<TOption>[] | undefined
    ) => HandledDropdownProps<TOption>;
    inputContainer: (children: ReactNode) => ReactNode;
    dropdownContainer: (children: ReactNode) => ReactNode;
}

export const useForm = <T extends object>({
    initialValue,
    valueToEdit,
    validators,
    simpleDirtyCheck,
    classes,
    setCleanup,
}: UseFormOptions<T>): UseFormReturn<T> => {
    const [errors, setErrors] = useState<FormErrors<T>>({});
    const [formData, setFormData] = useState<T>(initialValue);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [serializedInitialState, setSerializedInitialState] = useState(
        simpleDirtyCheck ? undefined : serializeToJsonIncludingUndefined(initialValue)
    );

    const hasErrors = useMemo(() => Object.keys(errors).length === 0, [errors]);

    const cleanUpForm = useCallback(() => {
        setIsFormDirty(false);
        setErrors({});
        setFormData({ ...initialValue });
    }, [initialValue]);

    useEffect(() => {
        if (!valueToEdit) return;
        onFormChange(valueToEdit, false);
    }, [valueToEdit]);

    useEffect(() => {
        if (simpleDirtyCheck || Boolean(serializedInitialState)) return;
        setSerializedInitialState(serializeToJsonIncludingUndefined(initialValue));
    }, [simpleDirtyCheck, initialValue, serializedInitialState]);

    useEffect(() => {
        if (!setCleanup) return;

        setCleanup(() => () => cleanUpForm());
    }, [setCleanup]);

    const resetFormDirty = () => {
        if (!simpleDirtyCheck)
            setSerializedInitialState(serializeToJsonIncludingUndefined(formData));
        setIsFormDirty(false);
    };

    const changeFormData = (change: (previousData: T) => T, affectsDirty = true) =>
        setFormData(previousState => {
            const newState = change(previousState);

            if (!affectsDirty) {
                if (!simpleDirtyCheck)
                    setSerializedInitialState(serializeToJsonIncludingUndefined(newState));
                return newState;
            }

            if (!simpleDirtyCheck)
                setIsFormDirty(
                    serializedInitialState !== serializeToJsonIncludingUndefined(newState)
                );
            else if (!isFormDirty) setIsFormDirty(true);

            return newState;
        });

    const onFormChange = (diff: Partial<T>, affectsDirty = true) => {
        changeFormData(previousState => ({ ...previousState, ...diff }), affectsDirty);

        const clearErrors = Object.fromEntries(Object.entries(diff).map(([k]) => [k, undefined]));
        const clearedErrors = { ...errors, ...clearErrors } as {
            [Key in keyof Partial<T>]: string;
        };
        setErrors(clearedErrors);
    };

    const validateForm = async (propertiesToValidate?: (keyof T)[]) =>
        await validateData(formData, propertiesToValidate, true);

    const validateData = async (
        data: T,
        propertiesToValidate?: (keyof T)[],
        changeState = true
    ) => {
        if (!validators) return false;

        const newErrors: FormErrors<T> = {};

        const propertiesWithValidators = Object.keys(validators) as (keyof T)[];
        for (const property of propertiesWithValidators) {
            if (propertiesToValidate !== undefined && !propertiesToValidate.includes(property))
                continue;

            const validatorList = validators[property];
            if (!validatorList) throw Error(`Failed to get validator with key ${String(property)}`);

            const valueToValidate = data[property] as keyof T;
            for (const validator of validatorList) {
                const error = await validator(valueToValidate, data);
                if (error) {
                    newErrors[property] = error;
                    break;
                }
            }
        }

        const errorKeys = Object.keys(newErrors);
        if (
            !errorKeys.length ||
            errorKeys.every(key => {
                const error: FormError | FormError[] = newErrors[key as keyof T]!;
                if (Array.isArray(error)) {
                    return error.length === 0;
                } else if (typeof error === "string") {
                    return error === "";
                } else {
                    return error.properties.length === 0;
                }
            })
        )
            return true;

        if (changeState) setErrors(newErrors);
        return false;
    };

    const resetDataToInitialState = () => {
        if (!serializedInitialState) return;

        const initialData = JSON.parse(serializedInitialState) as T;
        setFormData(initialData);
        setIsFormDirty(false);
    };

    const labelFor = (what: keyof T, label?: string) => {
        const key = String(what);
        return (
            <InputLabel
                labelFor={key}
                labelText={label ?? toTitleCase(key)}
                isValid={!errors[what]}
                className={classes?.label}
            />
        );
    };

    const inputPropsFor = (what: keyof T, valueConverter?: (inputValue: string) => any) => {
        const error = errors[what];
        const isValid = !error;
        return {
            id: what,
            name: what,
            value: formData[what],
            isValid,
            errorMessage: isValid ? undefined : <FormErrorDisplay error={error} />,
            type: "text",
            size: "large",
            className: classes?.input,
            onChange: e => {
                const { value } = e.target;
                onFormChange({
                    [what]: !valueConverter ? value : valueConverter(value),
                } as Partial<T>);
            },
        } as HandledInputProps;
    };

    const dropdownPropsFor = <TOption,>(
        what: keyof T,
        options: OptionType<TOption>[] | undefined
    ) => {
        const error = errors[what];
        const isValid = !error;
        return {
            id: what,
            name: what,
            options,
            size: "large",
            value: formData[what],
            placeholder: "Odaberite",
            isValid,
            errorMessage: isValid ? undefined : <FormErrorDisplay error={error} />,
            className: classes?.dropdown,
            noOptionsMessage: () => "Nema podataka",
            onChange: option =>
                onFormChange({
                    [what]: option,
                } as Partial<T>),
        } as HandledDropdownProps<TOption>;
    };

    const inputContainer = (input: ReactNode) =>
        !classes?.inputContainer ? input : <div className={classes.inputContainer}>{input}</div>;
    const dropdownContainer = (dropdown: ReactNode) =>
        !classes?.dropdownContainer ? (
            dropdown
        ) : (
            <div className={classes.dropdownContainer}>{dropdown}</div>
        );

    return {
        formData,
        setFormData,
        isFormDirty,
        errors,
        hasErrors,
        setErrors,
        onFormChange,
        changeFormData,
        validateForm,
        validateData,
        cleanUpForm,
        resetFormDirty,
        resetDataToInitialState,
        labelFor,
        inputPropsFor,
        dropdownPropsFor,
        inputContainer,
        dropdownContainer,
    };
};
